import './BuyerProfileDetailsSection.scss';
import { Avatar, Box, Typography } from '@mui/material';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CreateIcon from '@mui/icons-material/Create';
import ContactsIcon from '@mui/icons-material/Contacts';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import AddSpace from '../../../UtilityComponents/AddSpace/AddSpace';
import { ProfileVisibilityTypeEnum } from '../../../../enums/ProfileVisibilityTypeEnum';
import { useRef, useState } from 'react';
import { Buyer } from '../../../../Models/Buyer';
import { convertTimeStampToMonthAndYearFormat } from '../../../../utils/DateAndTimeUtils';
import { deepOrange } from '@mui/material/colors';
import GGModalWindow from '../../../UtilityComponents/GGModalWindow/GGModalWindow';
import BuyerProfileEditForm from '../BuyerProfileEditForm/BuyerProfileEditForm';
import { useNavigate } from 'react-router-dom';
import SnackBar, { SnackTypes } from '../../../../components/UtilityComponents/SnackBar/SnackBar';
import { SessionManager } from '../../../../services/SessionManager/SessionManager';
import { useDispatch } from 'react-redux';
import { openContactUsModal } from '../../../../redux/ContactUsModalSlice';
import GGButton from '../../../CommonComponents/GGButton/GGButton';
import ChangePasswordWindow from '../../AuthPageComponents/ChangePasswordPage/ChangePasswordWindow';

interface ProfileDetailsSectionPropTypes {
    visibility: ProfileVisibilityTypeEnum
    buyerObject: Buyer 
}

const BuyerProfileDetailsSection = ({visibility, buyerObject} : ProfileDetailsSectionPropTypes) => {
    const navigate = useNavigate()
    const dispatch = useDispatch();

    const [buyer, setBuyer] = useState<Buyer>(buyerObject)
    const [showEditModel, setShowEditModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showLoginSecurityModal, setShowLoginSecurityModal] = useState(false);
      // Snackbar based status handling 
    const snackBarRef = useRef<{ showSnackbar(): any }>(null)
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error
    });

    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state })
        snackBarRef.current?.showSnackbar()
    }

    const getFormattedTimeStamp = (): string => {
        let timeStamp = buyer?.createdAt
        if(timeStamp) {
            return convertTimeStampToMonthAndYearFormat(new Date(timeStamp))
        } else {
            return ""
        }
    }

    const yourOrdersButtonClicked = () => {
        navigate("/my-orders");
    }

    const editingEnded = (buyer: Buyer, didUpdate: boolean, wasUpdateSuccess: boolean) => {
        if (didUpdate && wasUpdateSuccess) {
            setBuyer(buyer)
            SessionManager.shared.buyer = buyer
            showSnackbar("Details updated successfully", SnackTypes.success);
        }
        setShowEditModal(false)
    }

    const LoginAndSecurityModalContent = 
         (
            <div>
                <Typography variant='body1'>Manage your login credentials and security settings here.</Typography>
                <Box display="flex" flexDirection="column" gap={3} mt={3}>
                    <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Email
                        </Typography>
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{
                                background: "#f5f5f5",
                                borderRadius: 2,
                                px: 2,
                                py: 1,
                                minHeight: 48,
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {buyer.email}
                            </Typography>
                        </Box>
                    </Box>
                    <Box>
                        <Box className="center">
                            <GGButton
                                variant="outlined"
                                btnColor='navy'
                                onClick={() => {
                                    setShowLoginSecurityModal(false)
                                    setShowChangePasswordModal(true)
                                }}
                                sx={{ ml: 'auto' }}
                            >
                                Change Password
                            </GGButton>
                        </Box>
                    </Box>
                </Box>
            </div>
        );

  return (
    <>
    <div className='client-profile-section'>
        <SnackBar ref={snackBarRef} severity={snackBarParams.state} alertDuration={5000} message={snackBarParams.message} />

        <GGModalWindow childView={<BuyerProfileEditForm buyer={buyer} exitEditPage={editingEnded}/>} open={showEditModel} onCloseHandler={() => setShowEditModal(false)} title={'Edit Profile'} />
        <GGModalWindow childView={<ChangePasswordWindow email={buyer.email} passwordChangeCompletion={() => setShowChangePasswordModal(false)} />} open={showChangePasswordModal} onCloseHandler={() => setShowChangePasswordModal(false)} title={'Change Password'} />
        <GGModalWindow
          open={showLoginSecurityModal}
          onCloseHandler={() => setShowLoginSecurityModal(false)}
          title="Login & Security"
          childView={LoginAndSecurityModalContent}
        />
        <AddSpace vSpace='10vh' hSpace='0px'/>

        <Typography variant='h5' fontWeight={500} paddingBottom={'20px'}> Profile & Settings </Typography>

        <div className='details-container'>
            <Avatar
                alt={buyer.fullName}
                src={buyer.profileImage}
                sx={{ width: 150, height: 150 }}
            >
                <Box sx={{height:"100%", width: "100%", bgcolor: deepOrange[500]}} className="center">
                    <Typography fontSize={'75px'} > {buyer.fullName[0]} </Typography> 
                </Box>
            </Avatar>

            <div className='text-details'>
                <Typography variant='h5' fontWeight={1000} paddingBottom={'10px'}> {buyer.fullName} </Typography>
                <Typography fontSize={'18px'}> Member since <b>{getFormattedTimeStamp()}</b> </Typography>
                <Typography fontSize={'18px'} textAlign={'justify'}> {buyer.about} </Typography>
            </div>

            <div className="edit-div">
                {visibility === ProfileVisibilityTypeEnum.MyProfile 
                ? 
                <CreateIcon id="edit-button" onClick={() => setShowEditModal(true)}/> : 
                <Box padding={"30px"}> </Box>}
            </div>
        </div>

        <div className='buttons-container'>
            <div className='button' onClick={yourOrdersButtonClicked}>
                <LocalMallIcon />  <Typography paddingLeft={'10px'}> Your Orders </Typography>
            </div>

            <div className='button' onClick={() => setShowLoginSecurityModal(true)}>
                <VpnKeyIcon />  <Typography paddingLeft={'10px'}> Login & Security </Typography>
            </div>

            <div className='button' onClick={() => {dispatch(openContactUsModal())}}>
                <ContactsIcon />  <Typography paddingLeft={'10px'}> Contact Us </Typography>
            </div>
        </div>
    </div>
    </>

  );
};

export default BuyerProfileDetailsSection;
